import React, { useEffect, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { X as XIcon, Trash2, Save, Loader2, Link2 as Overlap, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { apiClient } from '@/lib/api';
import { PL, StreamConfig, SocialPost, NarrativeCorrelation } from './types';
import { PostCard } from './PostCard';

type DetailTab = 'config' | 'detections' | 'overlaps';

const INTERVAL_OPTIONS = [15, 30, 60, 180, 360];

interface StreamDetailModalProps {
  stream: StreamConfig;
  onClose: () => void;
  onDelete: (id: string) => void;
  onUpdated: () => void;
}

export const StreamDetailModal: React.FC<StreamDetailModalProps> = ({ stream, onClose, onDelete, onUpdated }) => {
  const [tab, setTab] = useState<DetailTab>('config');

  // Config tab
  const [interval, setInterval_] = useState(stream.fetch_interval_minutes);
  const [emailAlerts, setEmailAlerts] = useState(stream.email_alerts_enabled ?? true);
  const [saving, setSaving] = useState(false);

  // Detections tab
  const [detections, setDetections] = useState<SocialPost[]>([]);
  const [detectionsLoading, setDetectionsLoading] = useState(false);
  const [detectionsCursor, setDetectionsCursor] = useState<string | null>(null);
  const [detectionsHasMore, setDetectionsHasMore] = useState(false);
  const [detectionsLoaded, setDetectionsLoaded] = useState(false);

  // Overlaps tab
  const [overlaps, setOverlaps] = useState<NarrativeCorrelation[]>([]);
  const [overlapsLoading, setOverlapsLoading] = useState(false);
  const [overlapsLoaded, setOverlapsLoaded] = useState(false);

  const loadDetections = async (before?: string) => {
    setDetectionsLoading(true);
    try {
      const res: any = await apiClient.getStreamDetections(stream.id, 25, before);
      setDetections(prev => before ? [...prev, ...(res?.detections ?? [])] : (res?.detections ?? []));
      setDetectionsCursor(res?.next_cursor ?? null);
      setDetectionsHasMore(!!res?.has_more);
    } catch { /* leave as-is */ }
    finally { setDetectionsLoading(false); setDetectionsLoaded(true); }
  };

  const loadOverlaps = async () => {
    setOverlapsLoading(true);
    try {
      const res: any = await apiClient.getStreamCorrelations(stream.id, undefined, 50);
      setOverlaps(res?.correlations ?? []);
    } catch { setOverlaps([]); }
    finally { setOverlapsLoading(false); setOverlapsLoaded(true); }
  };

  useEffect(() => {
    if (tab === 'detections' && !detectionsLoaded) loadDetections();
    if (tab === 'overlaps' && !overlapsLoaded) loadOverlaps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      await apiClient.updateStream(stream.id, { fetch_interval_minutes: interval, email_alerts_enabled: emailAlerts });
      onUpdated();
    } finally { setSaving(false); }
  };

  const acknowledgeOverlap = async (c: NarrativeCorrelation) => {
    if (c.status === 'acknowledged') return;
    try {
      await apiClient.acknowledgeStreamCorrelation(stream.id, c.id);
      setOverlaps(prev => prev.map(x => x.id === c.id ? { ...x, status: 'acknowledged' } : x));
    } catch { /* best-effort */ }
  };

  const expiresIn = stream.expires_at ? new Date(stream.expires_at).getTime() - Date.now() : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }} onClick={onClose}>
      <div className="rounded-xl overflow-hidden w-full max-w-2xl max-h-[85vh] flex flex-col" style={{ background: '#0f0f0f', border: '1px solid #00ff0020' }} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 flex-shrink-0" style={{ borderBottom: '1px solid #1a1a1a', background: '#00ff0008' }}>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full" style={{ background: stream.is_active ? '#00ff00' : '#374151', boxShadow: stream.is_active ? '0 0 6px #00ff00' : 'none' }} />
            <span className="text-sm font-mono font-bold" style={{ color: '#00ff00' }}>{stream.keyword}</span>
          </div>
          <button onClick={onClose} className="text-gray-600 hover:text-white"><XIcon size={14} /></button>
        </div>

        <div className="flex flex-shrink-0 px-5" style={{ borderBottom: '1px solid #1a1a1a' }}>
          {([['config', 'Config'], ['detections', 'Detections'], ['overlaps', 'Narrative Overlaps']] as const).map(([v, l]) => (
            <button key={v} onClick={() => setTab(v)}
              className="px-3 py-2.5 text-xs font-mono"
              style={{ borderBottom: tab === v ? '2px solid #00ff00' : '2px solid transparent', color: tab === v ? '#00ff00' : '#4b5563' }}>
              {l}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto">
          {tab === 'config' && (
            <div className="p-5 space-y-4">
              <div className="space-y-2">
                {[
                  ['Platforms', stream.platforms.map(p => PL[p] ?? p).join(', ')],
                  ['Total Posts', String(stream.total_posts ?? 0)],
                  ['Created', stream.created_at ? new Date(stream.created_at).toLocaleDateString() : '—'],
                  ['Last Fetch', stream.last_fetch_at ? formatDistanceToNow(new Date(stream.last_fetch_at), { addSuffix: true }) : 'Never'],
                ].map(([l, v]) => (
                  <div key={l} className="flex items-center justify-between py-1.5" style={{ borderBottom: '1px solid #1a1a1a' }}>
                    <span className="text-xs text-gray-500">{l}</span>
                    <span className="text-xs font-mono text-white">{v}</span>
                  </div>
                ))}
                {expiresIn != null && (
                  <div className="flex items-center justify-between py-1.5" style={{ borderBottom: '1px solid #1a1a1a' }}>
                    <span className="text-xs text-gray-500">Auto-expires</span>
                    <span className="text-xs font-mono" style={{ color: expiresIn <= 0 ? '#ef4444' : expiresIn < 6 * 3_600_000 ? '#f97316' : '#9ca3af' }}>
                      {expiresIn <= 0 ? 'Expired — reactivate below to renew' : `in ${Math.round(expiresIn / 3_600_000)}h (toggling active renews it)`}
                    </span>
                  </div>
                )}
              </div>

              <div>
                <p className="text-[10px] font-mono text-gray-600 uppercase tracking-wider mb-2">Check interval</p>
                <Select value={String(interval)} onValueChange={v => setInterval_(Number(v))}>
                  <SelectTrigger className="h-8 text-xs" style={{ background: '#111', border: '1px solid #222', color: '#e5e7eb' }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent style={{ background: '#111', border: '1px solid #333' }}>
                    {INTERVAL_OPTIONS.map(m => (
                      <SelectItem key={m} value={String(m)} style={{ color: '#e5e7eb' }}>
                        {m < 60 ? `Every ${m} min` : `Every ${m / 60}h`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <label className="flex items-center gap-1.5 text-xs text-gray-400 cursor-pointer">
                <input type="checkbox" checked={emailAlerts} onChange={e => setEmailAlerts(e.target.checked)} className="w-3.5 h-3.5 accent-[#00ff00]" />
                Email me on new activity
              </label>

              <div className="flex gap-2 pt-2">
                <Button className="flex-1 h-8 text-xs font-mono" disabled={saving}
                  style={{ background: '#00ff0015', border: '1px solid #00ff0030', color: '#00ff00' }}
                  onClick={saveConfig}>
                  {saving ? <Loader2 size={11} className="mr-1.5 animate-spin" /> : <Save size={11} className="mr-1.5" />}
                  Save
                </Button>
                <Button className="h-8 text-xs font-mono px-3"
                  style={{ background: '#ef444415', border: '1px solid #ef444430', color: '#ef4444' }}
                  onClick={() => { onDelete(stream.id); onClose(); }}>
                  <Trash2 size={11} />
                </Button>
              </div>
            </div>
          )}

          {tab === 'detections' && (
            <div className="p-5">
              {detectionsLoading && detections.length === 0 && <p className="text-xs text-gray-600 font-mono">Loading detection history...</p>}
              {!detectionsLoading && detections.length === 0 && (
                <p className="text-xs text-gray-600">No posts detected by this stream yet. The background worker checks every {stream.fetch_interval_minutes} minutes.</p>
              )}
              {detections.map(post => (
                <PostCard key={post.id} post={post} keyword={stream.keyword} onAnalyze={() => {}} showLinkedAccounts={false} />
              ))}
              {detectionsHasMore && (
                <Button className="w-full h-8 text-xs font-mono mt-2" disabled={detectionsLoading}
                  style={{ background: '#111', border: '1px solid #222', color: '#9ca3af' }}
                  onClick={() => detectionsCursor && loadDetections(detectionsCursor)}>
                  {detectionsLoading ? <Loader2 size={11} className="mr-1.5 animate-spin" /> : null}
                  Load more
                </Button>
              )}
            </div>
          )}

          {tab === 'overlaps' && (
            <div className="p-5 space-y-2">
              {overlapsLoading && <p className="text-xs text-gray-600 font-mono">Loading...</p>}
              {!overlapsLoading && overlaps.length === 0 && (
                <div className="flex flex-col items-center py-10">
                  <Overlap size={24} className="text-gray-700 mb-2" />
                  <p className="text-xs text-gray-600">No narrative overlaps detected for this stream yet.</p>
                </div>
              )}
              {overlaps.map(c => (
                <div key={c.id} className="rounded-lg p-3 flex items-start justify-between gap-3"
                  style={{ background: '#111', border: `1px solid ${c.status === 'new' ? '#eab30830' : '#1e1e1e'}` }}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded font-mono" style={{ background: '#eab30820', color: '#eab308' }}>
                        {Math.round(c.overlap_score * 100)}%
                      </span>
                      <span className="text-xs text-white truncate">also in "{c.other_keyword}"</span>
                    </div>
                    <p className="text-[10px] text-gray-600">{new Date(c.detected_at).toLocaleString()}</p>
                  </div>
                  {c.status === 'new' ? (
                    <button onClick={() => acknowledgeOverlap(c)} className="text-[10px] px-2 py-1 rounded font-mono flex-shrink-0 flex items-center gap-1"
                      style={{ background: '#00ff0010', border: '1px solid #00ff0030', color: '#00ff00' }}>
                      <CheckCircle2 size={10} /> Ack
                    </button>
                  ) : (
                    <span className="text-[9px] text-gray-600 flex-shrink-0">Acknowledged</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StreamDetailModal;
